import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, MessageSquare, Mail, ChevronDown } from 'lucide-react';
import client from '../../api/client';

const BADGE = {
  delivered: ['Delivered', 'bg-[#E8F5E9] text-[#2E7D32] border-[#C8E6C9]'],
  pending: ['Sent', 'bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]'],
  stale: ['Sent, no receipt', 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]'],
  undelivered: ['Undelivered', 'bg-[#FFE4EC] text-[#B4235A] border-[#F8C3D3]'],
  bounced: ['Bounced', 'bg-[#FFE4EC] text-[#B4235A] border-[#F8C3D3]'],
  failed: ['Failed', 'bg-[#FFE4EC] text-[#B4235A] border-[#F8C3D3]'],
  blocked: ['Blocked', 'bg-[#FFF8E1] text-[#B45309] border-[#FCE3A6]'],
};
const FILTERS = [['', 'All'], ['delivered', 'Delivered'], ['pending', 'Awaiting receipt'], ['problem', 'Problems']];

export const badgeKey = (m) => {
  if (m.status !== 'sent') return m.status;
  if (m.delivery === 'pending' && Date.now() - new Date(m.created_at).getTime() > 24 * 3600e3) return 'stale';
  return m.delivery || 'pending';
};

const Row = ({ m, compact }) => {
  const [open, setOpen] = useState(false);
  const [label, cls] = BADGE[badgeKey(m)] || BADGE.pending;
  const Icon = m.channel === 'email' ? Mail : MessageSquare;
  return (
    <li data-testid={`message-log-row-${m.id}`} className="py-2.5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-start gap-3 text-left">
        <Icon className="w-4 h-4 mt-0.5 text-[#9A8A7E] shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-[#2A2A2A] truncate">
            {!compact && <strong className="font-medium">{m.guest_name || 'Guest'}</strong>}{!compact && ' · '}
            <span className="text-[#6A6A6A]">{m.kind_label}</span>
          </p>
          <p className="text-[11px] text-[#9A8A7E] break-all">{new Date(m.created_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })} · {m.to}</p>
          {m.error && <p data-testid={`message-log-error-${m.id}`} className="text-[11px] text-[#B4235A] mt-0.5">{m.error}</p>}
        </div>
        <span data-testid={`message-log-status-${m.id}`} className={`text-[10px] border px-2 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{label}</span>
        <ChevronDown className={`w-3.5 h-3.5 mt-0.5 text-[#9A8A7E] transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="ml-7 mt-2 p-3 rounded-xl bg-[#FBF7F2] border border-[#F2E8DE] text-xs text-[#3A3A3A] whitespace-pre-wrap">
          <p className="text-[10px] text-[#9A8A7E] mb-1">To {m.guest_name || 'guest'} at {m.to} · {m.kind_label}</p>
          {m.subject && <p className="font-medium mb-1">Subject: {m.subject}</p>}
          {m.body || '(no text body)'}
          {m.delivery_at && <p className="text-[10px] text-[#9A8A7E] mt-2">Receipt: {m.delivery} at {new Date(m.delivery_at).toLocaleString()}{m.delivery_detail ? ` (${m.delivery_detail})` : ''}</p>}
        </div>
      )}
    </li>
  );
};

export const MessageLog = ({ guestId, compact = false, initialStatus = '' }) => {
  const [status, setStatus] = useState(initialStatus);
  const [channel, setChannel] = useState('');
  const [q, setQ] = useState('');
  const [d, setD] = useState(null);
  const [more, setMore] = useState(false);

  const load = useCallback(async (skip = 0) => {
    const params = { status, channel, q, skip, limit: compact ? 20 : 50, ...(guestId ? { guest_id: guestId } : {}) };
    const { data } = await client.get('/message-log', { params });
    setD((prev) => (skip && prev ? { ...data, messages: [...prev.messages, ...data.messages] } : data));
  }, [status, channel, q, guestId, compact]);
  useEffect(() => { const t = setTimeout(() => load().catch(() => setD({ messages: [], total: 0, counts: {} })), 250); return () => clearTimeout(t); }, [load]);

  const c = d?.counts || {};
  const count = (k) => (k === '' ? Object.values(c).reduce((a, b) => a + b, 0) : k === 'problem' ? (c.failed || 0) + (c.blocked || 0) + (c.undelivered || 0) + (c.bounced || 0) : c[k] || 0);
  return (
    <div data-testid={guestId ? `guest-message-history-${guestId}` : 'message-log'}>
      {!compact && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {FILTERS.map(([k, l]) => (
            <button key={k || 'all'} data-testid={`message-log-filter-${k || 'all'}`} onClick={() => setStatus(k)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${status === k ? 'bg-[#7B2C3B] text-white border-[#7B2C3B]' : 'bg-white border-[#EFE3D8] text-[#3A3A3A] hover:border-[#B8896C]'}`}>
              {l} <span className="opacity-70">{count(k)}</span>
            </button>
          ))}
          <select data-testid="message-log-channel" value={channel} onChange={(e) => setChannel(e.target.value)} className="text-xs px-2 py-1.5 rounded-full border border-[#EFE3D8] bg-white">
            <option value="">Texts + emails</option><option value="sms">Texts only</option><option value="email">Emails only</option>
          </select>
          <div className="relative ml-auto">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-[#9A8A7E]" />
            <input data-testid="message-log-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Guest or number" className="text-xs pl-8 pr-3 py-1.5 rounded-full border border-[#EFE3D8] w-44" />
          </div>
        </div>
      )}
      {!d ? <Loader2 className="w-4 h-4 animate-spin text-[#7B2C3B]" /> : (
        <>
          <ul className="divide-y divide-[#F2E8DE]">
            {d.messages.map((m) => <Row key={m.id} m={m} compact={compact} />)}
          </ul>
          {!d.messages.length && <p data-testid="message-log-empty" className="text-sm text-[#9A8A7E] py-4">{guestId ? 'No messages sent to this guest yet.' : 'No messages match. Every text and email to guests will appear here.'}</p>}
          {d.messages.length < d.total && (
            <button data-testid="message-log-more" disabled={more} onClick={async () => { setMore(true); await load(d.messages.length); setMore(false); }} className="mt-2 text-xs text-[#7B2C3B] hover:underline">
              {more ? 'Loading…' : `Show more (${d.total - d.messages.length} older)`}
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default MessageLog;
